// Manual Entry pipeline node (app.md §9 Manual Entry, v1.30). Invoices marked at
// step 5 for manual Point Click Care entry — or split across companies, which has
// no single export file — route here after the last approval step instead of
// Export Control's file path. The gallery is a plain list: the only card action is
// Open, because the AP clerk needs the full details (and the PDF) in front of them
// to key the invoice into Point Click Care. The modal carries the actions: a green
// Confirm ('approved' → 'Manually Entered Into Point Click Care', terminal — no
// file, no export batch, no Closing Confirmation) behind an are-you-sure dialog,
// and a red Deny (required comment → Denial Review), mirroring Export Control.
// Both write a decision — the audit trail — and both apply immediately.
import { useMemo, useState } from 'react'
import { Body1, Button, Toast, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { CheckmarkCircleRegular, DismissRegular } from '@fluentui/react-icons'
import type { Invoice } from '../lib/types'
import type { DecisionOutcome as DecisionOutcomeValue, ReasonCategory } from '../lib/constants'
import { DecisionOutcome, DecisionStep, InvoiceStatus, TOASTER_ID } from '../lib/constants'
import { createDecision } from '../lib/data'
import { errorMessage } from '../api/errors'
import { canDecide, type Session } from '../lib/identity'
import { useQueue } from '../lib/hooks'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { DecisionDialog } from '../components/DecisionDialog'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { useDecisionButtonStyles } from '../lib/theme'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'
import { ScreenTitle } from '../components/ScreenTitle'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
})

type Pending = { kind: 'confirm' | 'deny'; invoice: Invoice } | null

interface Props {
  session: Session
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ManualEntryScreen({ session, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const btn = useDecisionButtonStyles()
  const user = session.user
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingManualEntry], refreshNonce)
  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])

  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const [pending, setPending] = useState<Pending>(null)
  const [submitting, setSubmitting] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  const notAuthorizedReason =
    "You don't hold the Manual Entry role. You can view this invoice, but your decision would be refused."

  async function submit(inv: Invoice, outcome: DecisionOutcomeValue, reason?: string, category?: string) {
    setSubmitting(true)
    setActionError(null)
    try {
      await createDecision({
        invoice_id: inv.id,
        step: DecisionStep.ManualEntry,
        decision: outcome,
        reason: reason || undefined,
        reason_category: (category as ReasonCategory) || undefined,
        row_version: inv.row_version,
      })
      setPending(null)
      setOpenInvoice(null)
      void reload()
      onQueuesChanged?.()
      dispatchToast(
        <Toast>
          <ToastTitle>
            {outcome === DecisionOutcome.Approved
              ? 'Manually entered into Point Click Care — invoice on its way to payment'
              : 'Invoice denied — routed to Denial Review'}
          </ToastTitle>
        </Toast>,
        { intent: 'success' },
      )
    } catch (e) {
      // The API's refusal wording is the message (an active hold, the wrong
      // status, no authority at this step).
      setActionError(errorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>Manual Entry</ScreenTitle>
        <Body1 className={s.sub}>For each invoice, manually enter invoice details into Point Click Care.</Body1>
      </div>

      <ErrorBanner message={error || actionError} />

      {loading ? (
        <Loading label="Loading invoices for manual entry…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : undefined} />
      ) : (
        <InvoiceQueue invoices={filtered} columns="amounts" onOpen={setOpenInvoice} />
      )}

      {/* The same GL controls Export Control has, per the authority matrix and
          D24: Manual Entry is where a mixed-company invoice is keyed by hand
          instead of exported, so it is the terminal accounting step and needs the
          same override. It is also the step D23's gate lands on when every
          approval tier was unstaffed — an invoice can arrive with every cost
          center null and no CSV to catch it, so "Who is it for?" has to be
          answerable here. The server enforces both grants; this is the surface. */}
      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf
        user={user}
        editAccounts
        editCostCenters
        onClose={() => setOpenInvoice(null)}
        actions={
          openInvoice &&
          (() => {
            const decidable = canDecide(session, DecisionStep.ManualEntry, openInvoice)
            return (
              <>
                <Button
                  className={btn.approve}
                  icon={<CheckmarkCircleRegular />}
                  disabled={submitting || !decidable}
                  title={decidable ? 'Confirm this invoice was entered into Point Click Care' : notAuthorizedReason}
                  onClick={() => setPending({ kind: 'confirm', invoice: openInvoice })}
                >
                  Manually Entered Into Point Click Care
                </Button>
                <Button
                  className={btn.deny}
                  icon={<DismissRegular />}
                  disabled={submitting || !decidable}
                  title={decidable ? 'Deny' : notAuthorizedReason}
                  onClick={() => setPending({ kind: 'deny', invoice: openInvoice })}
                >
                  Deny
                </Button>
              </>
            )
          })()
        }
      />

      <DecisionDialog
        open={!!pending}
        title={pending?.kind === 'confirm' ? 'Are you sure?' : 'Deny this invoice?'}
        body={
          !pending
            ? undefined
            : pending.kind === 'confirm'
              ? 'You are confirming that this invoice has been manually entered into Point Click Care, meaning that it is on its way to payment. This will remove this invoice from the pipeline here. You cannot undo this action.'
              : 'Denied invoices route to Denial Review. This comment is recorded on the invoice and is visible in the decision history.'
        }
        requireReason={pending?.kind === 'deny'}
        reasonLabel="Reason for denial"
        showReasonCategory={pending?.kind === 'deny'}
        confirmLabel={pending?.kind === 'confirm' ? 'Manually Entered Into Point Click Care' : 'Deny'}
        confirmClassName={pending?.kind === 'confirm' ? btn.approve : undefined}
        destructive={pending?.kind === 'deny'}
        submitting={submitting}
        onCancel={() => setPending(null)}
        onConfirm={(reason, category) =>
          pending &&
          submit(pending.invoice, pending.kind === 'confirm' ? DecisionOutcome.Approved : DecisionOutcome.Denied, reason, category)
        }
      />
    </div>
  )
}
